import React, { useEffect, useState } from "react";
import { Clock, Anchor } from "lucide-react";
import { TESTIDS } from "@/lib/testIds";

/**
 * Compact sidebar Ship Time chip — surfaces the authoritative operational
 * clock (BR-TIME-001) on every workspace. When the ship is docked at a
 * port, an "All Aboard" countdown appears below the clock — a critical
 * operational cue for guests ashore.
 *
 * Reads from `today.time` (composed read model) and `today.current_port_call`
 * for the docked-port depart-at time. Everything ticks locally each
 * second via a lightweight interval so no request storms.
 */
export default function SidebarShipTime({ today }) {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 15_000); // 15s tick — sidebar is quiet
    return () => clearInterval(id);
  }, []);

  const shipTz = today?.time?.ship_time?.timezone;
  if (!shipTz) return null;

  // Format Ship Time locally in the ship's timezone.
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: shipTz,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).formatToParts(now);
  const clock = `${parts.find((p) => p.type === "hour").value}:${parts.find((p) => p.type === "minute").value} ${parts.find((p) => p.type === "dayPeriod").value}`;
  const tzAbbrev = today.time.ship_time.timezone_abbrev || "";

  // All-aboard countdown — only when docked at a port with a known depart time.
  const currentPc = today.current_port_call;
  const isDocked = currentPc?.kind === "port" && currentPc?.depart_at;
  let allAboardLabel = null;
  let allAboardTime = null;
  if (isDocked) {
    const depart = new Date(currentPc.depart_at);
    // "All aboard" is convention ~30 min before departure.
    const allAboard = new Date(depart.getTime() - 30 * 60_000);
    const mins = Math.floor((allAboard.getTime() - now.getTime()) / 60_000);
    if (mins > 0) {
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      allAboardLabel = h > 0 ? `${h}h ${m}m` : `${m}m`;
      allAboardTime = new Intl.DateTimeFormat("en-US", {
        timeZone: shipTz,
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }).format(allAboard);
    }
  }

  return (
    <div
      data-testid={TESTIDS.sidebarShipTime}
      className="rounded-2xl border border-white/10 bg-white/[0.03] p-3"
    >
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center shrink-0">
          <Clock className="w-4 h-4 text-[#5aa8ff]" />
        </div>
        <div className="leading-tight flex-1 min-w-0">
          <div className="cc-overline text-white/50">Ship Time</div>
          <div className="text-[15px] font-bold tabular-nums flex items-baseline gap-1">
            {clock}
            <span className="text-[10px] font-medium text-white/50">{tzAbbrev}</span>
          </div>
        </div>
      </div>
      {allAboardLabel && (
        <div
          data-testid={TESTIDS.sidebarAllAboard}
          className="mt-2 pt-2 border-t border-white/10 flex items-center gap-2 text-[11px]"
        >
          <Anchor className="w-3 h-3 text-[#F59E0B] shrink-0" />
          <div className="flex-1 leading-tight">
            <div className="font-semibold text-[#F59E0B]">All Aboard</div>
            <div className="text-[10px] text-white/60 tabular-nums">
              {allAboardTime} · in {allAboardLabel}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
